'use client'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { signIn } from 'supertokens-web-js/lib/build/recipe/emailpassword'
import { FormButton } from '../Form/FormButton'
import { FormInput } from '../Form/FormInput'
import { PasswordInput } from '../Form/PasswordInput'

type EmailSignInFormData = {
  email: string
  password: string
}

type SignInFormProps = {
  onSignInSuccessAction?: (method: string) => void
  onSubmitStart?: (method: string) => void
  onError?: (error: string, method: string) => void
}

export default function SignInForm({
  onSignInSuccessAction,
  onSubmitStart,
  onError,
}: SignInFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailSignInFormData>()

  const [apiFieldErrors, setApiFieldErrors] = useState<EmailSignInFormData>({
    email: '',
    password: '',
  })

  const onSubmit = async ({ email, password }: EmailSignInFormData) => {
    onSubmitStart?.('email')
    try {
      const response = await signIn({
        formFields: [
          {
            id: 'email',
            value: email,
          },
          {
            id: 'password',
            value: password,
          },
        ],
      })

      switch (response.status) {
        case 'OK':
          onSignInSuccessAction?.('email')
          break
        case 'FIELD_ERROR':
          setApiFieldErrors({
            email: response.formFields.find((field) => field.id === 'email')?.error || '',
            password: response.formFields.find((field) => field.id === 'password')?.error || '',
          })
          onError?.('Field validation error', 'email')
          break
        case 'WRONG_CREDENTIALS_ERROR':
          toast.error('Email password combination is incorrect.')
          onError?.('Wrong credentials', 'email')
          break
        case 'SIGN_IN_NOT_ALLOWED':
          console.error(response.reason)
          toast.error('Oops! Something went wrong.')
          onError?.(response.reason, 'email')
          break
      }
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        toast.error(err.message)
      } else {
        toast.error('Oops! Something went wrong.')
      }
      onError?.(err.isSuperTokensGeneralError ? err.message : 'Unknown error', 'email')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-[32px]">
      <div className="flex flex-col gap-[9px]">
        <FormInput
          type="email"
          placeholder="Email"
          autoComplete="email"
          {...register('email', { required: 'Email is required' })}
          error={errors.email?.message}
          apiError={apiFieldErrors.email}
        />

        <PasswordInput
          placeholder="Password"
          autoComplete="current-password"
          {...register('password', { required: 'Password is required' })}
          error={errors.password?.message}
          apiError={apiFieldErrors.password}
        />
      </div>

      <FormButton type="submit" className="w-full">
        Sign In
      </FormButton>
    </form>
  )
}
